import { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { useSalonScopedFetch } from "../../../hooks/useSalonScopedFetch";
import { deepEqual } from "../../../utils/deepEqual";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { BusinessInfo, SalonContextType } from "../../../types/context";
import {
  NotificationCategory,
  NotificationPreferenceSetting,
  NotificationPreferencesUpdate,
  StaffNotificationPreferences,
} from "../../../types/notificationPreferences";

/** Edits keyed by staff_user_id; the server response is the baseline. */
type StaffById = Record<string, StaffNotificationPreferences>;

export interface UseNotificationPreferencesResult {
  categories: NotificationCategory[];
  /** Staff in server order, with any unsaved edits applied. */
  staff: StaffNotificationPreferences[];
  isLoading: boolean;
  isSaving: boolean;
  loadError: string | null;
  isDirty: boolean;
  setPreference: (
    staffUserId: string,
    categoryKey: string,
    setting: NotificationPreferenceSetting,
  ) => void;
  setEmailOptIn: (staffUserId: string, optedIn: boolean) => void;
  save: () => Promise<boolean>;
  discard: () => void;
  retry: () => void;
}

const indexByStaffId = (staff: StaffNotificationPreferences[]): StaffById =>
  Object.fromEntries(staff.map((entry) => [entry.staff_user_id, entry]));

/**
 * Build the minimal PUT payload: only staff whose values changed, and for each
 * only the categories / email switch that changed. Untouched categories are not
 * sent, so they keep following the role default on the server.
 */
export const buildUpdates = (
  original: StaffById,
  draft: StaffById,
): NotificationPreferencesUpdate[] =>
  Object.values(draft).flatMap((edited) => {
    const saved = original[edited.staff_user_id];
    if (!saved) return [];
    const changedPreferences = Object.fromEntries(
      Object.entries(edited.preferences).filter(
        ([key, value]) => saved.preferences[key] !== value,
      ),
    );
    const update: NotificationPreferencesUpdate = {
      staff_user_id: edited.staff_user_id,
    };
    if (Object.keys(changedPreferences).length > 0) {
      update.preferences = changedPreferences;
    }
    if (
      edited.email_notifications_opt_in !== saved.email_notifications_opt_in
    ) {
      update.email_notifications_opt_in = edited.email_notifications_opt_in;
    }
    return Object.keys(update).length > 1 ? [update] : [];
  });

/**
 * State for Settings -> Notifications: loads the matrix once per location,
 * tracks unsaved edits against the server baseline (dirty state, like
 * WorkflowPermissionsModal), and saves only what changed.
 */
export function useNotificationPreferences({
  enabled = true,
}: { enabled?: boolean } = {}): UseNotificationPreferencesResult {
  const { getNotificationPreferences, updateNotificationPreferences } =
    useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const salonId = (businessInfo as BusinessInfo | null)?.id ?? null;

  const [categories, setCategories] = useState<NotificationCategory[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [original, setOriginal] = useState<StaffById>({});
  const [draft, setDraft] = useState<StaffById>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getNotificationPreferences();
      const byId = indexByStaffId(response.staff);
      setCategories(response.categories);
      setOrder(response.staff.map((entry) => entry.staff_user_id));
      setOriginal(byId);
      setDraft(byId);
    } catch (error) {
      console.error("Error loading notification preferences:", error);
      setLoadError("We couldn't load notification settings.");
    } finally {
      setIsLoading(false);
    }
  }, [getNotificationPreferences]);

  const { retry } = useSalonScopedFetch(salonId, load, {
    enabled,
    tag: "notificationPreferences",
  });

  const isDirty = useMemo(() => !deepEqual(original, draft), [original, draft]);

  // Warn before a refresh / tab close drops unsaved edits.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const updateStaff = useCallback(
    (
      staffUserId: string,
      change: (
        entry: StaffNotificationPreferences,
      ) => StaffNotificationPreferences,
    ) =>
      setDraft((prev) =>
        prev[staffUserId]
          ? { ...prev, [staffUserId]: change(prev[staffUserId]) }
          : prev,
      ),
    [],
  );

  const setPreference = useCallback(
    (
      staffUserId: string,
      categoryKey: string,
      setting: NotificationPreferenceSetting,
    ) =>
      updateStaff(staffUserId, (entry) => ({
        ...entry,
        preferences: { ...entry.preferences, [categoryKey]: setting },
      })),
    [updateStaff],
  );

  const setEmailOptIn = useCallback(
    (staffUserId: string, optedIn: boolean) =>
      updateStaff(staffUserId, (entry) => ({
        ...entry,
        email_notifications_opt_in: optedIn,
      })),
    [updateStaff],
  );

  const discard = useCallback(() => setDraft(original), [original]);

  const save = useCallback(async (): Promise<boolean> => {
    const updates = buildUpdates(original, draft);
    if (updates.length === 0) return true;
    setIsSaving(true);
    try {
      const response = await updateNotificationPreferences({ updates });
      const saved = { ...original, ...indexByStaffId(response.staff) };
      setOriginal(saved);
      setDraft(saved);
      notifySuccess("Notification settings saved.");
      return true;
    } catch (error) {
      // Keep the edits so nothing is lost and the user can adjust and retry.
      // fetchWithAuth puts the API's user-facing message on the Error.
      console.error("Error saving notification preferences:", error);
      notifyError(
        error instanceof Error && error.message
          ? error.message
          : "We couldn't save notification settings. Please try again.",
        { title: "Notification settings not saved" },
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [original, draft, updateNotificationPreferences]);

  const staff = useMemo(
    () => order.map((id) => draft[id]).filter(Boolean),
    [order, draft],
  );

  return {
    categories,
    staff,
    isLoading,
    isSaving,
    loadError,
    isDirty,
    setPreference,
    setEmailOptIn,
    save,
    discard,
    retry,
  };
}
